import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const ShopManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shops, setShops] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState("");
  const [paymentModelFilter, setPaymentModelFilter] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState("");
  const [selectedShop, setSelectedShop] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [error, setError] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin");
        return false;
      }
      return true;
    } catch (error) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const fetchShops = useCallback(
    async (page = 1) => {
      const token = localStorage.getItem("ogestyle_token");
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({ page, limit: 50 });
        if (search) params.append("search", search);
        if (paymentModelFilter)
          params.append("paymentModel", paymentModelFilter);
        if (visibilityFilter) params.append("visibility", visibilityFilter);
        if (subscriptionFilter)
          params.append("subscriptionStatus", subscriptionFilter);

        const response = await axios.get(
          `${API_BASE_URL}/api/admin/shops?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setShops(response.data.shops);
        setPagination(response.data.pagination);
      } catch (error) {
        setError("Failed to load shops");
      } finally {
        setLoading(false);
      }
    },
    [search, paymentModelFilter, visibilityFilter, subscriptionFilter],
  );

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchShops();
    }
  }, [checkPortalAdmin, fetchShops]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchShops(1);
  };

  const viewShopDetails = async (shopId) => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/shops/${shopId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setSelectedShop(response.data);
      setShowModal(true);
    } catch (error) {
      setError("Failed to load shop details");
    }
  };

  const toggleVisibility = async (shopId, isVisible) => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/shops/${shopId}/visibility`,
        { isPubliclyVisible: !isVisible },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMessage({ type: "success", text: `Shop visibility updated` });
      fetchShops(pagination.page);
    } catch (error) {
      setMessage({ type: "error", text: "Failed to update visibility" });
    }
  };

  const extendTrial = async (shopId) => {
    const token = localStorage.getItem("ogestyle_token");

    // Validate additionalDays
    if (isNaN(extendDays) || extendDays === 0 || extendDays < -90 || extendDays > 90) {
      setMessage({
        type: "error",
        text: "Please enter a valid number of days (-90 to 90, excluding 0)",
      });
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/shops/${shopId}/extend-trial`,
        { additionalDays: extendDays },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMessage({
        type: "success",
        text: extendDays > 0 
          ? `Trial extended by ${extendDays} days`
          : `Trial reduced by ${Math.abs(extendDays)} days`,
      });
      setShowExtendModal(false);
      setSelectedShop(null);
      fetchShops(pagination.page);
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to extend trial",
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getPaymentModelBadge = (model) => {
    const styles = {
      TRIAL: { bg: "#fef3c7", color: "#92400e", label: "🎁 Trial" },
      SUBSCRIPTION: {
        bg: "#d1fae5",
        color: "#065f46",
        label: "💳 Subscription",
      },
      TRANSACTION: { bg: "#ede9fe", color: "#5b21b6", label: "📊 Transaction" },
    };
    const style = styles[model] || {
      bg: "#f3f4f6",
      color: "#6b7280",
      label: model,
    };

    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: "9999px",
          fontSize: "12px",
          fontWeight: "500",
          background: style.bg,
          color: style.color,
        }}
      >
        {style.label}
      </span>
    );
  };

  const getSubscriptionStatusBadge = (status) => {
    const styles = {
      active: { bg: "#d1fae5", color: "#065f46", label: "✓ Active" },
      past_due: { bg: "#fee2e2", color: "#991b1b", label: "⚠ Past Due" },
      canceled: { bg: "#f3f4f6", color: "#6b7280", label: "✗ Canceled" },
      none: { bg: "#f3f4f6", color: "#6b7280", label: "- None" },
    };
    const style = styles[status] || {
      bg: "#f3f4f6",
      color: "#6b7280",
      label: status || "None",
    };

    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: "9999px",
          fontSize: "12px",
          fontWeight: "500",
          background: style.bg,
          color: style.color,
        }}
      >
        {style.label}
      </span>
    );
  };

  if (loading && shops.length === 0) {
    return (
      <PortalAdminLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div className="spinner"></div>
          <p>Loading shops...</p>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "24px",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{ fontSize: "28px", fontWeight: "700", margin: "0 0 8px" }}
          >
            🏪 Shop Management
          </h1>
          <p style={{ color: "#6b7280", margin: 0 }}>
            View and manage all shop locations
          </p>
        </div>

        {message.text && (
          <div
            style={{
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "24px",
              background: message.type === "success" ? "#d1fae5" : "#fee2e2",
              border: `1px solid ${message.type === "success" ? "#10b981" : "#ef4444"}`,
              color: message.type === "success" ? "#065f46" : "#991b1b",
            }}
          >
            {message.text}
          </div>
        )}

        {error && (
          <div
            style={{
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "24px",
              background: "#fee2e2",
              border: "1px solid #ef4444",
              color: "#991b1b",
            }}
          >
            {error}
          </div>
        )}

        {/* Stats Summary */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#3b82f6" }}
            >
              {shops.filter((s) => s.paymentModel === "TRIAL").length}
            </div>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>On Trial</div>
          </div>
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#10b981" }}
            >
              {shops.filter((s) => s.subscriptionStatus === "active").length}
            </div>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>Subscribed</div>
          </div>
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#f59e0b" }}
            >
              {shops.filter((s) => s.isPubliclyVisible).length}
            </div>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>Visible</div>
          </div>
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#6b7280" }}
            >
              {pagination.total}
            </div>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>
              Total Shops
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "24px",
          }}
        >
          <form
            onSubmit={handleSearch}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              alignItems: "flex-end",
            }}
          >
            <div style={{ flex: "1 1 250px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Search
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by shop name or city..."
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </div>
            <div style={{ width: "150px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Payment Model
              </label>
              <select
                value={paymentModelFilter}
                onChange={(e) => setPaymentModelFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="">All Models</option>
                <option value="TRIAL">Trial</option>
                <option value="SUBSCRIPTION">Subscription</option>
                <option value="TRANSACTION">Transaction</option>
              </select>
            </div>
            <div style={{ width: "150px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Visibility
              </label>
              <select
                value={visibilityFilter}
                onChange={(e) => setVisibilityFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="">All</option>
                <option value="visible">Visible</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
            <div style={{ width: "150px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                  fontSize: "14px",
                }}
              >
                Subscription
              </label>
              <select
                value={subscriptionFilter}
                onChange={(e) => setSubscriptionFilter(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="past_due">Past Due</option>
                <option value="canceled">Canceled</option>
              </select>
            </div>
            <button
              type="submit"
              style={{
                padding: "10px 24px",
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              🔍 Search
            </button>
          </form>
        </div>

        {/* Shops Table */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#f9fafb",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <th
                    style={{
                      textAlign: "left",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Shop
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Business
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Payment Model
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Subscription
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Trial Days
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Visible
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: "14px 16px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {shops.map((shop) => (
                  <tr
                    key={shop._id}
                    style={{ borderBottom: "1px solid #f3f4f6" }}
                  >
                    <td style={{ padding: "14px 16px" }}>
                      <div>
                        <div style={{ fontWeight: "600", color: "#1a1a1a" }}>
                          {shop.name}
                        </div>
                        <div style={{ fontSize: "13px", color: "#6b7280" }}>
                          {shop.city}
                          {shop.state ? `, ${shop.state}` : ""}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "14px 16px" }}>
                      <div
                        style={{
                          fontWeight: "500",
                          color: shop.businessActive ? "#1a1a1a" : "#ef4444",
                        }}
                      >
                        {shop.businessName}
                        {!shop.businessActive && (
                          <span style={{ fontSize: "11px" }}> (Inactive)</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "center" }}>
                      {getPaymentModelBadge(shop.paymentModel)}
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "center" }}>
                      {getSubscriptionStatusBadge(shop.subscriptionStatus)}
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "center" }}>
                      {shop.paymentModel === "TRIAL" &&
                      shop.trialDaysRemaining !== null ? (
                        <span
                          style={{
                            fontWeight: "600",
                            color:
                              shop.trialDaysRemaining <= 7
                                ? "#ef4444"
                                : shop.trialDaysRemaining <= 14
                                  ? "#f59e0b"
                                  : "#10b981",
                          }}
                        >
                          {shop.trialDaysRemaining} days
                        </span>
                      ) : (
                        <span style={{ color: "#6b7280" }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "center" }}>
                      <button
                        onClick={() =>
                          toggleVisibility(shop._id, shop.isPubliclyVisible)
                        }
                        style={{
                          padding: "6px 12px",
                          borderRadius: "9999px",
                          border: "none",
                          fontSize: "12px",
                          fontWeight: "500",
                          cursor: "pointer",
                          background: shop.isPubliclyVisible
                            ? "#d1fae5"
                            : "#fee2e2",
                          color: shop.isPubliclyVisible ? "#065f46" : "#991b1b",
                        }}
                      >
                        {shop.isPubliclyVisible ? "👁 Visible" : "🚫 Hidden"}
                      </button>
                    </td>
                    <td style={{ padding: "14px 16px", textAlign: "center" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          onClick={() => viewShopDetails(shop._id)}
                          style={{
                            padding: "6px 12px",
                            background: "#eff6ff",
                            color: "#1e40af",
                            border: "1px solid #bfdbfe",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Details
                        </button>
                        {shop.paymentModel === "TRIAL" && (
                          <button
                            onClick={() => {
                              setSelectedShop(shop);
                              setShowExtendModal(true);
                            }}
                            style={{
                              padding: "6px 12px",
                              background: "#fef3c7",
                              color: "#92400e",
                              border: "1px solid #fcd34d",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Extend
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div
              style={{
                padding: "16px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    onClick={() => fetchShops(page)}
                    style={{
                      padding: "8px 14px",
                      borderRadius: "6px",
                      border:
                        pagination.page === page
                          ? "2px solid #3b82f6"
                          : "1px solid #e5e7eb",
                      background:
                        pagination.page === page ? "#eff6ff" : "white",
                      color: pagination.page === page ? "#1e40af" : "#6b7280",
                      fontWeight: pagination.page === page ? "600" : "400",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    {page}
                  </button>
                ),
              )}
            </div>
          )}
        </div>

        {/* Shop Details Modal */}
        {showModal && selectedShop && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "800px",
                maxHeight: "90vh",
                overflow: "auto",
                padding: "32px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "24px",
                }}
              >
                <div>
                  <h2
                    style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      margin: "0 0 4px",
                    }}
                  >
                    {selectedShop.shop?.name}
                  </h2>
                  <p style={{ color: "#6b7280", margin: 0 }}>
                    {selectedShop.shop?.street}
                    {selectedShop.shop?.city
                      ? `, ${selectedShop.shop.city}`
                      : ""}
                    {selectedShop.shop?.state
                      ? `, ${selectedShop.shop.state}`
                      : ""}
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    color: "#6b7280",
                    padding: "8px",
                  }}
                >
                  ×
                </button>
              </div>

              {/* Shop Details */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "16px",
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{
                    background: "#f9fafb",
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    Payment Model
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    {getPaymentModelBadge(selectedShop.shop?.paymentModel)}
                  </div>
                </div>
                <div
                  style={{
                    background: "#f9fafb",
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    Subscription Status
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    {getSubscriptionStatusBadge(
                      selectedShop.shop?.subscriptionStatus,
                    )}
                  </div>
                </div>
                <div
                  style={{
                    background: "#f9fafb",
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    Trial End
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      marginTop: "8px",
                    }}
                  >
                    {formatDate(selectedShop.shop?.trialEndDate)}
                  </div>
                </div>
                <div
                  style={{
                    background: "#f9fafb",
                    borderRadius: "8px",
                    padding: "16px",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    Created
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      marginTop: "8px",
                    }}
                  >
                    {formatDate(selectedShop.shop?.createdAt)}
                  </div>
                </div>
              </div>

              {/* Staff at Shop */}
              {selectedShop.staff?.length > 0 && (
                <div
                  style={{
                    background: "#f9fafb",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "20px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      marginBottom: "16px",
                    }}
                  >
                    Staff ({selectedShop.staff.length})
                  </h3>
                  <div style={{ display: "grid", gap: "8px" }}>
                    {selectedShop.staff.map((staff, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: "12px",
                          background: "white",
                          borderRadius: "8px",
                          border: "1px solid #e5e7eb",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: "600" }}>
                            {staff.userId?.name}
                          </div>
                          <div style={{ fontSize: "13px", color: "#6b7280" }}>
                            {staff.userId?.email}
                          </div>
                        </div>
                        <span
                          style={{
                            padding: "4px 10px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "500",
                            background: staff.isFreelancer
                              ? "#fef3c7"
                              : "#d1fae5",
                            color: staff.isFreelancer ? "#92400e" : "#065f46",
                          }}
                        >
                          {staff.isFreelancer ? "Freelancer" : "Employee"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Booking Stats */}
              {selectedShop.bookingStats?.length > 0 && (
                <div
                  style={{
                    background: "#f9fafb",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "20px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      marginBottom: "16px",
                    }}
                  >
                    Booking Statistics
                  </h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(120px, 1fr))",
                      gap: "16px",
                    }}
                  >
                    {selectedShop.bookingStats.map((stat, idx) => (
                      <div key={idx} style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "700",
                            color: "#3b82f6",
                          }}
                        >
                          {stat.count}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            textTransform: "capitalize",
                          }}
                        >
                          {stat._id}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#10b981",
                          }}
                        >
                          {formatCurrency(stat.revenue)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => setShowModal(false)}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "#f3f4f6",
                  color: "#6b7280",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "500",
                  fontSize: "14px",
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Extend Trial Modal */}
        {showExtendModal && selectedShop && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "400px",
                padding: "32px",
              }}
            >
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  marginBottom: "8px",
                }}
              >
                Adjust Trial Period
              </h2>
              <p style={{ color: "#6b7280", marginBottom: "24px" }}>
                Adjust trial for <strong>{selectedShop.name}</strong>. Use positive numbers to extend, negative to reduce.
              </p>

              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Additional Days
                </label>
                <input
                  type="number"
                  min="-90"
                  max="90"
                  value={extendDays}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value)) {
                      setExtendDays(Math.min(90, Math.max(-90, value)));
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "16px",
                  }}
                />
                <p
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    marginTop: "8px",
                  }}
                >
                  Current trial ends: {formatDate(selectedShop.trialEndDate)}
                </p>
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => extendTrial(selectedShop._id)}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "#3b82f6",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  ✓ Update Trial
                </button>
                <button
                  onClick={() => {
                    setShowExtendModal(false);
                    setSelectedShop(null);
                    setExtendDays(7);
                  }}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "#f3f4f6",
                    color: "#6b7280",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "500",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PortalAdminLayout>
  );
};

export default ShopManagement;
